import type { ButtonProps } from "@nuxt/ui";
import { throttle } from "es-toolkit";

export interface Flash {
  alert?: string;
  notice?: string;
  action?: ButtonProps;
}

const toast = useToast();

// throttle due to https://github.com/inertiajs/inertia/issues/1091
// without this, we get TWO simultaneous toasts if the first page has flash.
export const flash = throttle(flash0, 5);

function flash0(message: string | Flash) {
  // First remove any existing toasts to prevent stacking
  gracefulClearToasts();

  if (isString(message)) {
    message = { notice: message };
  }

  const title = message.alert || message.notice;
  if (!title) return;

  const color = message.alert ? "error" : "primary";

  const duration = howManySecondsForMessage(title) * 1000;

  const actions = message.action ? [message.action] : undefined;
  toast.add({
    actions,
    color,
    duration,
    icon: Icons.exclamation,
    orientation: "horizontal",
    progress: false,
    title,
  });
}

// toast.clear() does not properly reset the offset within the Toaster
// which results in toasts being placed lower and lower on the page.
// Need to call remove() instead.
function gracefulClearToasts() {
  toast.toasts.value.forEach((t) => {
    toast.remove(t.id);
  });
}

function howManySecondsForMessage(message: string): number {
  // The average person reads 2-300 words per minute. I've also seen "4 words per second" as a
  // recommendation, which would be 240. Using this slow number gives the user some cushion to
  // find the alert on screen.
  const WORDS_PER_MINUTE = 200;
  const number_of_words = message.split(" ").length;
  return Math.floor(clamp((number_of_words / WORDS_PER_MINUTE) * 60, 5, 20));
}
